import { numFormatObj } from 'src/utils/util';
import {
    checkIfValidQuantities,
    getTotalExtendedPrice,
    getTotalQuantity,
} from './eventHandlerUtil';

const invalidProductMsg = 'Product not found';
const invalidBundleItemMsg =
    'The entered Bundle type is not supported in MyCase and should be faxed to Customer Care.';
const invalidSPSitemMsg = 'Item cannot be added. Contact Customer Care.';
const emptyBundleMsg = 'Bundle should contain at least one product.';

export const getModifiedProcedureResponse = (procedureData) => {
    return Object.fromEntries(
        Object.entries(procedureData).map((entry) => [
            entry[0],
            entry[1] instanceof Array
                ? entry[1].map((x) => ({
                      ...x,
                      description:
                          x.description === 'MazorX Robot Used'
                              ? 'MazorX Robot Used'
                              : x.description,
                      isSelected: null,
                      isMedtronicSelected: null,
                      isCompetitiveSelected: null,
                  }))
                : entry[1],
        ])
    );
};

export const getModifiedBundleChildItems = (
    existingItem,
    pricedItemsArr,
    bundleHeadersArr
) => {
    return existingItem.children.map((child) => {
        let updatedChildItemObj = { ...child };
        const pricedChildItemIdx = pricedItemsArr
            .map((x) => x.productCFN)
            .findIndex((y) => y === child.cfn);
        if (pricedChildItemIdx >= 0) {
            updatedChildItemObj = {
                ...pricedItemsArr[pricedChildItemIdx],
                cfn: child.cfn,
                lotSerial: child.lotSerial,
                salesOrg: pricedItemsArr[pricedChildItemIdx].salesOrg,
            };
            updatedChildItemObj.basePrice = null;
            if (pricedItemsArr[pricedChildItemIdx].salesOrg === 'S030') {
                updatedChildItemObj.isInvalidItemMsg =
                    'Tissue items can\'t be added as bundled item.';
            } else if (
                pricedItemsArr[pricedChildItemIdx].exclusionStatus ===
                'Item number not found.'
            ) {
                updatedChildItemObj.isInvalidItemMsg = invalidProductMsg;
            } else if (pricedItemsArr[pricedChildItemIdx].exclusionStatus) {
                updatedChildItemObj.isInvalidItemMsg =
                    pricedItemsArr[pricedChildItemIdx].exclusionStatus;
            }
        } else if (bundleHeadersArr.includes(child.cfn)) {
            updatedChildItemObj.isInvalidItemMsg =
                'Bundle cannot be added as item.';
        } else if (child.cfn.startsWith('SPS')) {
            // eslint-disable-next-line no-undef
            updatedItemObj.isInvalidItemMsg = invalidSPSitemMsg;
        } else {
            updatedChildItemObj.isInvalidItemMsg = invalidProductMsg;
        }
        return updatedChildItemObj;
    });
};

export const getModifiedItemTableData = (
    existingItemsArr,
    pricedItemsArr,
    bundleHeadersArr,
    chkreportpayment
) => {
    return existingItemsArr.map((existingItem) => {
        let updatedItemObj = { ...existingItem };
        if (
            (chkreportpayment === true &&
                existingItem.cfn.toUpperCase().startsWith('M2')) ||
            existingItem.cfn.toUpperCase().startsWith('M1') ||
            existingItem.cfn.toUpperCase().startsWith('BUN') ||
            existingItem.cfn.toUpperCase().startsWith('KEN')
        ) {
            updatedItemObj.isInvalidItemMsg = 'Bundle items cannot be added';
        } else {
            const pricedItemIdx = pricedItemsArr
                .map((x) => x.productCFN)
                .findIndex((y) => y === existingItem.cfn);
            if (pricedItemIdx >= 0) {
                updatedItemObj = {
                    ...pricedItemsArr[pricedItemIdx],
                    cfn: existingItem.cfn,
                    lotSerial:
                        pricedItemsArr[pricedItemIdx].salesOrg === 'S030'
                            ? [{ used: false }]
                            : existingItem.lotSerial,
                    accountOwned: existingItem.accountOwned
                        ? existingItem.accountOwned
                        : false,
                    salesOrg: pricedItemsArr[pricedItemIdx].salesOrg,
                };
                if (existingItem.isBundleItem) {
                    updatedItemObj.isBundleItem = existingItem.isBundleItem;
                    updatedItemObj.children = existingItem.children;
                }
                updatedItemObj.extPrice = getTotalExtendedPrice(
                    pricedItemsArr[pricedItemIdx].basePrice
                        ? pricedItemsArr[pricedItemIdx].basePrice
                        : '',
                    updatedItemObj.lotSerial,
                    'total'
                );
                if (
                    bundleHeadersArr &&
                    bundleHeadersArr.includes(existingItem.cfn)
                ) {
                    if (
                        existingItem.children &&
                        existingItem.children.length > 0
                    ) {
                        // bundle child items validation
                        updatedItemObj.children = getModifiedBundleChildItems(
                            existingItem,
                            pricedItemsArr,
                            bundleHeadersArr
                        );
                    } else {
                        updatedItemObj.lotSerial = existingItem.lotSerial.map(
                            (x) => ({
                                ...x,
                                isInvalidQtyMsg: emptyBundleMsg,
                            })
                        );
                    }
                } else if (
                    pricedItemsArr[pricedItemIdx].exclusionStatus ===
                    'Item number not found.'
                ) {
                    updatedItemObj.isInvalidItemMsg = invalidProductMsg;
                } else {
                    updatedItemObj.isInvalidItemMsg = null;
                }
            } else if (existingItem.cfn.startsWith('SPS')) {
                updatedItemObj.isInvalidItemMsg = invalidSPSitemMsg;
            } else if (
                bundleHeadersArr &&
                bundleHeadersArr.includes(existingItem.cfn) &&
                existingItem.cfn.startsWith('M1')
            ) {
                updatedItemObj.isInvalidItemMsg = invalidBundleItemMsg;
            } else {
                updatedItemObj.isInvalidItemMsg = invalidProductMsg;
            }
        }
        return updatedItemObj;
    });
};

export const getPriceInNumberDataType = (price) => {
    if (typeof price === 'number') {
        return parseFloat(price);
    }
    if (price) {
        const symbolRemovedPrice = price.replaceAll(/[^\d.,]/g, '');
        const seperators = price.match(/[.,]/g) || [];
        
        let decimalSeperator = '';
        let thousandSeperator = '';
        
        // Check if currency has no decimal places (e.g., JPY, KRW)
        if (numFormatObj.minimumFractionDigits === 0) {
            // For zero-decimal currencies, we need to distinguish between:
            // - Thousands separators: exactly 3 digits after (e.g., "122,845" or "1,234,567")
            // - Decimal separators: 1-2 digits after (e.g., "122845.00") - should be truncated
            if (seperators.length > 0) {
                const lastSeparator = seperators[seperators.length - 1];
                const lastSeparatorIndex = symbolRemovedPrice.lastIndexOf(lastSeparator);
                const digitsAfterLastSeparator = symbolRemovedPrice.substring(lastSeparatorIndex + 1);
                
                // If exactly 3 digits after last separator, it's a thousands separator
                // If 1-2 digits after, it's likely a decimal that should be truncated
                if (digitsAfterLastSeparator.length === 3) {
                    // This is a thousands separator (e.g., "122,845" -> 122845)
                    thousandSeperator = lastSeparator;
                } else if (digitsAfterLastSeparator.length >= 1 && digitsAfterLastSeparator.length <= 2) {
                    // This is a decimal separator (e.g., "122845.00" -> 122845)
                    decimalSeperator = lastSeparator;
                    thousandSeperator = seperators.length > 1 ? seperators[0] : '';
                } else {
                    // All separators are thousands separators
                    thousandSeperator = seperators[0];
                }
            }
        } else if (seperators.length > 0) {
            // Last separator is decimal separator
            decimalSeperator = seperators.pop() || '';
            thousandSeperator = seperators.length > 0 ? seperators[0] : '';
        }

        let numString =
            decimalSeperator !== ''
                ? symbolRemovedPrice
                      .replaceAll(thousandSeperator, '')
                      .replace(decimalSeperator, '.')
                : symbolRemovedPrice.replaceAll(thousandSeperator, '');
        
        // For zero-decimal currencies, truncate any decimal portion
        if (numFormatObj.minimumFractionDigits === 0 && numString.includes('.')) {
            numString = Math.floor(parseFloat(numString)).toString();
        }
        
        return parseFloat(numString);
    }
};

export const getValidateItemsArr = (productsList) => {
    const bundleItems = productsList.filter(
        (item) => item.isBundleItem && item.children && item.children.length > 0
    );
    const bundleItemChildren =
        bundleItems && bundleItems.length > 0
            ? bundleItems.reduce((prev, curr) => prev.concat(curr.children), [])
            : [];
    const bundleChildrenQty = bundleItemChildren.map(
        (child) =>
            child.lotSerial &&
            child.lotSerial.map(
                (i) =>
                    (i.Other || i.other ? Number(i.Other || i.other) : 0) +
                    (i.Used || i.used ? Number(i.Used || i.used) : 0)
            )
    );
    const bundleChildrenLotSerial = bundleItemChildren.map(
        (child) => child.lotSerial && child.lotSerial.map((i) => i.Serial)
    );
    const arrProductCFN = productsList
        .map((item) => item.cfn)
        .concat(bundleItemChildren.map((x) => x.cfn));
    const arrLotSerialNumber = productsList
        .map((item) => item.lotSerial && item.lotSerial.map((i) => i.Serial))
        .concat(bundleChildrenLotSerial);
    const arrQuantity = productsList
        .map(
            (item) =>
                item.lotSerial &&
                item.lotSerial.map(
                    (i) =>
                        (i.Other || i.other ? Number(i.Other || i.other) : 0) +
                        (i.Used || i.used ? Number(i.Used || i.used) : 0)
                )
        )
        .concat(bundleChildrenQty);

    return arrProductCFN.map((cfn, i) => ({
        lotSerialNumber: arrLotSerialNumber[i].toString(),
        productCFN: cfn,
        quantity: arrQuantity[i].toString(),
    }));
};

export const getItemTableDataWithQtyValidation = (itemTableData) => {
    return itemTableData.map((obj) => {
        const updatedLotSerialArr = obj.lotSerial.map((x) => ({
            ...x,
            isInvalidQtyMsg:
                !obj.isInvalidItemMsg &&
                (obj.isBundleItem
                    ? (!obj.children ||
                          (obj.children && obj.children.length === 0)) &&
                      emptyBundleMsg
                    : !checkIfValidQuantities(x)
                    ? 'Qty Validation Error'
                    : null),
        }));
        return {
            ...obj,
            lotSerial: updatedLotSerialArr,
        };
    });
};

export const getValidatedBundleChildren = (
    existingChildren,
    validatedItems
) => {
    const validItemsCFN = validatedItems.map((x) => x.productCFN);
    return existingChildren.map((child) => {
        const isLotSerialReqd =
            (child.isLot || child.isSerialRequired) &&
            validatedItems.filter((val) => child.cfn === val.productCFN)
                .length > 0 &&
            validatedItems.filter(
                (val) =>
                    child.cfn ===
                    Object.prototype.hasOwnProperty.call(val.productCFN)[0],
                'lotSerialData'
            );
        const validatedLotSerialArr =
            isLotSerialReqd &&
            validatedItems.filter((val) => child.cfn === val.productCFN)[0]
                .lotSerialData;

        const updatedLotSerialArr = isLotSerialReqd
            ? child.lotSerial.map((x) =>
                  validatedLotSerialArr.find(
                      (vLot) =>
                          x.Serial &&
                          x.Serial.length > 0 &&
                          vLot.lotSerialNumber === x.Serial &&
                          vLot.statusCode === '10712'
                  )
                      ? {
                            ...x,
                            isInvalidLotSerialMsg: child.isLot
                                ? 'Invalid Lot Number'
                                : 'Invalid Serial Number',
                        }
                      : validatedLotSerialArr.find(
                            (vLot) =>
                                (!x.Serial ||
                                    (x.Serial && x.Serial.length === 0)) &&
                                vLot.statusCode === '10712'
                        )
                      ? {
                            ...x,
                            isInvalidLotSerialMsg: child.isLot
                                ? 'Lot number required'
                                : 'Serial number required',
                        }
                      : { ...x, isInvalidLotSerialMsg: null }
              )
            : child.lotSerial.map((x) => ({
                  ...x,
                  isInvalidLotSerialMsg: null,
              }));

        return {
            ...child,
            isInvalidItemMsg: !validItemsCFN.includes(child.cfn)
                ? invalidProductMsg
                : null,
            isLotSerialReqd: isLotSerialReqd,
            lotSerial: updatedLotSerialArr,
        };
    });
};

export const getValidatedItemTableData = (existingItems, validatedItems) => {
    const validItemsCFN = validatedItems.map((x) => x.productCFN);
    return existingItems.map((obj) => {
        const isLotSerialReqd =
            (obj.isLot || obj.isSerialRequired) &&
            validatedItems.filter((val) => obj.cfn === val.productCFN).length >
                0 &&
            validatedItems.filter(
                (val) =>
                    obj.cfn ===
                    Object.prototype.hasOwnProperty.call(val.productCFN)[0],
                'lotSerialData'
            );
        const validatedLotSerialArr =
            isLotSerialReqd &&
            validatedItems.filter((val) => obj.cfn === val.productCFN)[0]
                .lotSerialData;

        const updatedLotSerialArr = isLotSerialReqd
            ? obj.lotSerial.map((x) =>
                  validatedLotSerialArr.find(
                      (vLot) =>
                          x.Serial &&
                          x.Serial.length > 0 &&
                          vLot.lotSerialNumber === x.Serial &&
                          vLot.statusCode === '10712'
                  )
                      ? {
                            ...x,
                            isInvalidLotSerialMsg: obj.isLot
                                ? 'Invalid Lot Number'
                                : 'Invalid Serial Number',
                        }
                      : validatedLotSerialArr.find(
                            (vLot) =>
                                (!x.Serial ||
                                    (x.Serial && x.Serial.length === 0)) &&
                                vLot.statusCode === '10712'
                        )
                      ? {
                            ...x,
                            isInvalidLotSerialMsg: obj.isLot
                                ? 'Lot number required'
                                : 'Serial number required',
                        }
                      : { ...x, isInvalidLotSerialMsg: null }
              )
            : obj.lotSerial.map((x) => ({ ...x, isInvalidLotSerialMsg: null }));

        const validatedItemObj = {
            ...obj,
            isInvalidItemMsg: !validItemsCFN.includes(obj.cfn)
                ? invalidProductMsg
                : null,
            isLotSerialReqd: isLotSerialReqd,
            lotSerial: updatedLotSerialArr,
        };
        if (obj.isBundleItem && obj.children && obj.children.length > 0) {
            const updatedChildrenArr = getValidatedBundleChildren(
                obj.children,
                validatedItems
            );
            validatedItemObj.children = updatedChildrenArr;
        }
        return validatedItemObj;
    });
};

export const getNewItemObj = (
    newItem,
    existingItem,
    isNew,
    bundleHeadersArr
) => {
    const lotSerialArr = [{ used: false }];
    const newItemObj = {
        cfn: newItem.value,
        dailyRate: '',
        description: '',
        salesOrg: '',
        uom: '',
        accountOwned: false,
        lotSerial: lotSerialArr,
        extPrice: getTotalExtendedPrice('', lotSerialArr, 'total'),
        isInvalidItemMsg:
            existingItem && existingItem.isInvalidItemMsg === invalidProductMsg
                ? null
                : existingItem && existingItem.isInvalidItemMsg,
    };

    if (newItem.isBundle || bundleHeadersArr?.includes(newItem.value)) {
        newItemObj.isBundleItem = true;
        newItemObj.isBundleFromAddItem = true;
        newItemObj.lotSerial = [{ Used: '1', isInvalidQtyMsg: null }];
        newItemObj.isInvalidItemMsg =
            bundleHeadersArr?.includes(newItem.value) &&
            newItem.value.startsWith('M1')
                ? invalidBundleItemMsg
                : null;
        newItemObj.children = isNew
            ? []
            : existingItem.children &&
              newItemObj.isInvalidItemMsg !== invalidBundleItemMsg
            ? existingItem.children
            : [];
    }
    return newItemObj;
};

export const getValidReplenishGridData = (productsList) => {
    const replenItems = [];
    let nonTissueItems = productsList.filter(
        (x) => x.salesOrg === 'S002' && !x.isBundleItem
    );
    const bundleItems = productsList.filter(
        (item) => item.isBundleItem && item.children && item.children.length > 0
    );
    const bundleChildren =
        bundleItems && bundleItems.length > 0
            ? bundleItems
                  .reduce((prev, curr) => prev.concat(curr.children), [])
                  .filter((x) => x.salesOrg === 'S002')
            : [];
    nonTissueItems = nonTissueItems.concat(bundleChildren);
    if (nonTissueItems.length > 0) {
        nonTissueItems.forEach((item) => {
            if (!replenItems.map((x) => x.cfn).includes(item.cfn)) {
                replenItems.push({
                    ...item,
                    isSelected: false,
                    replenQty: getTotalQuantity(item.lotSerial),
                });
            } else {
                const itemIdx = replenItems.findIndex(
                    (x) => x.cfn === item.cfn
                );
                if (itemIdx >= 0) {
                    replenItems[itemIdx].replenQty = getTotalQuantity([
                        ...replenItems[itemIdx].lotSerial,
                        ...item.lotSerial,
                    ]);
                }
            }
        });
    }
    return replenItems;
};

export const getPriceOverrideTableData = (itemTableData) => {
    const tissueItems = itemTableData.filter(
        (item) => item.salesOrg === 'S030' && !item.accountOwned
    );
    const nonTissueItems = itemTableData.filter(
        (item) => item.salesOrg === 'S002' && !item.accountOwned
    );
    return [...tissueItems, ...nonTissueItems];
};

export const getUpdatedPriceOverridenData = (
    existingTableData,
    priceOverridenData
) => {
    return existingTableData.map((existingItem) => {
        let updatedItemObj = { ...existingItem };
        const priceOverwrittenItemIdx = priceOverridenData
            .map((x) => x.cfn)
            .findIndex((y) => y === existingItem.cfn);
        if (priceOverwrittenItemIdx >= 0) {
            updatedItemObj = {
                ...priceOverridenData[priceOverwrittenItemIdx],
            };
        }
        return updatedItemObj;
    });
};
